import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeChoice = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeState {
  choice: ThemeChoice;
  resolved: ResolvedTheme;
  setChoice: (c: ThemeChoice) => void;
  cycle: () => void;
  toggle: () => void;
}

const Ctx = createContext<ThemeState | null>(null);
export const THEME_STORAGE_KEY = 'cevop.theme';

const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: '#F7F8FA',
  dark: '#0a0a0a',
};

const CYCLE: ThemeChoice[] = ['system', 'light', 'dark'];

function readMatchMediaDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

export function readStoredThemeChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* ignore */
  }
  return 'system';
}

export function resolveTheme(choice: ThemeChoice, systemDark: boolean): ResolvedTheme {
  return choice === 'system' ? (systemDark ? 'dark' : 'light') : choice;
}

export function applyDocumentTheme(resolved: ResolvedTheme) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', resolved);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLORS[resolved]);
}

function nextChoice(choice: ThemeChoice): ThemeChoice {
  return CYCLE[(CYCLE.indexOf(choice) + 1) % CYCLE.length]!;
}

/**
 * Theme provider. Defaults to the OS setting, remembers an explicit choice, and stamps
 * `data-theme` on <html> so the token layer switches. Persisting a preference is a
 * convenience, not a credential — safe in localStorage.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readStoredThemeChoice);
  const [sysDark, setSysDark] = useState(readMatchMediaDark);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => setSysDark(mql.matches);
    on();
    mql.addEventListener('change', on);
    return () => mql.removeEventListener('change', on);
  }, []);

  const resolved = resolveTheme(choice, sysDark);

  useEffect(() => {
    applyDocumentTheme(resolved);
  }, [resolved]);

  const setChoice = useCallback((c: ThemeChoice) => {
    setChoiceState(c);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, c);
    } catch {
      /* ignore */
    }
  }, []);

  const cycle = useCallback(() => {
    setChoiceState((current) => {
      const next = nextChoice(current);
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const value = useMemo<ThemeState>(
    () => ({
      choice,
      resolved,
      setChoice,
      cycle,
      toggle: () => setChoice(resolved === 'dark' ? 'light' : 'dark'),
    }),
    [choice, resolved, setChoice, cycle],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTheme must be used inside <ThemeProvider>');
  return v;
}
